import { TimelineLayout } from '../layoutConstants'
import ActionTypes from '../action_types'
import { TimelineItemType, Cursor, Dimensions, HandleCursor } from '../types/types'

export type ScrubberDisplay = {
  margin: [number | null, number, number, number]
  height: number
  handleWidth: number
  handleHeight: number
  leftHandleCursor: HandleCursor
  rightHandleCursor: HandleCursor
  surface: {
    cursor: Cursor
  }
  viewport: {
    cursor: Cursor
  }
}

export type Scroller = {
  left: number | null
  right: number | null
}

export type TimelineDisplay = {
  axisMargin: number
}

export type HorizontalTimelineState = {
  // denotes whether the initial setup has taken place (e.g. the initial zoom level has been set based on the size of the timeline)
  initialized: boolean

  // inertia/freescrolling variables
  freeScrolling: boolean

  // the width/height of the stage (in pixels)
  size: Dimensions

  // the scroller object. Defines current scroll position. This should probably be set on app boot by using the stage size and scrubber size
  scroller: Scroller

  // the data to actually draw the timeline
  timelineDisplay: TimelineDisplay
  // the data to draw the timeline scrubber
  scrubberDisplay: ScrubberDisplay
  drilledDownItem: { id: number; timelineItemType: TimelineItemType } | null | undefined
}

export const initialState: HorizontalTimelineState = {
  initialized: false,
  freeScrolling: false,
  size: {
    width: 0,
    height: 0,
  },
  scroller: {
    left: null,
    right: null,
  },
  timelineDisplay: {
    axisMargin: TimelineLayout.AXIS_MARGIN,
  },
  scrubberDisplay: {
    margin: [
      null,
      TimelineLayout.RIGHT_BOUNDARY_PADDING,
      TimelineLayout.BOTTOM_BOUNDARY_PADDING,
      TimelineLayout.LEFT_BOUNDARY_PADDING,
    ],
    height: TimelineLayout.HANDLE_HEIGHT,
    handleWidth: TimelineLayout.HANDLE_WIDTH,
    handleHeight: TimelineLayout.HANDLE_HEIGHT,
    leftHandleCursor: 'auto',
    rightHandleCursor: 'auto',
    surface: {
      cursor: 'auto',
    },
    viewport: {
      cursor: 'auto',
    },
  },
  drilledDownItem: null,
}

export type Action = any // placeholder, actual shape is inferred from actions

const reducer = (state = initialState, action: Action) => {
  switch (action.type) {
    case ActionTypes.INITIALIZE:
      return initialize(state, action)
    case ActionTypes.SIZE_CHANGE:
      return changeSize(state, action)
    case ActionTypes.CANCEL_FREE_SCROLL:
      return cancelFreeScroll(state, action)
    case ActionTypes.START_FREE_SCROLL:
      return startFreeScroll(state, action)
    case ActionTypes.UPDATE_LEFT_AND_RIGHT:
      return updateLeftAndRight(state, action)
    case ActionTypes.TIMELINE_ITEM_DRILL_DOWN:
      return timelineItemDrillDown(state, action)
    case ActionTypes.TIMELINE_ITEM_DRILL_OUT:
      return timelineItemDrillOut(state, action)
    default:
      return state
  }
}

function initialize(draft: HorizontalTimelineState, action: Action) {
  const { left, right } = action.payload ?? {}
  draft.initialized = true
  draft.scroller.left = left
  draft.scroller.right = right
  return draft
}

function changeSize(draft: HorizontalTimelineState, action: Action) {
  const { size } = action.payload ?? {}
  draft.size.width = size?.width ?? draft.size.width
  draft.size.height = size?.height ?? draft.size.height
  return draft
}

function startFreeScroll(draft: HorizontalTimelineState, _action: Action) {
  draft.freeScrolling = true
  return draft
}

function cancelFreeScroll(draft: HorizontalTimelineState, _action: Action) {
  draft.freeScrolling = false
  return draft
}

function updateLeftAndRight(draft: HorizontalTimelineState, action: Action) {
  const { left, right } = action.payload ?? {}
  draft.scroller.left = left
  draft.scroller.right = right
  return draft
}

function timelineItemDrillDown(draft: HorizontalTimelineState, action: Action) {
  const { id, timelineItemType } = action.payload ?? {}
  draft.drilledDownItem = { id, timelineItemType }
  return draft
}

function timelineItemDrillOut(draft: HorizontalTimelineState, _action: Action) {
  draft.drilledDownItem = null
  return draft
}

export default reducer
